/**
 * User Form Page
 *
 * Mobile-first form for creating and editing users.
 */

import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { ArrowLeft, Shield, ShieldAlert, Camera, BookOpen } from 'lucide-react'

import { useUser, useCreateUser, useUpdateUser } from '@/hooks/useUsers'
import { parseApiError } from '@/lib/utils'
import { t } from '@/i18n'
import { UserRole } from '@/types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { Checkbox } from '@/components/ui/checkbox'
import { UserAvatar, AvatarSelector } from '@/components/AvatarSelector'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const createUserSchema = (isEditing: boolean) =>
  z.object({
    full_name: z.string().min(1, t('validation.required')),
    email: z.string().min(1, t('validation.required')).email(t('validation.invalidEmail')),
    role: z.enum(['admin', 'seller', 'accountant']),
    password: isEditing
      ? z.string().min(8, t('validation.minLength', { min: 8 })).optional().or(z.literal(''))
      : z.string().min(8, t('validation.minLength', { min: 8 })),
    is_active: z.boolean(),
  })

type UserFormValues = z.infer<ReturnType<typeof createUserSchema>>

const roles = [
  { value: 'admin', label: () => t('users.admin'), icon: ShieldAlert, color: 'text-red-500' },
  { value: 'seller', label: () => t('users.seller'), icon: Shield, color: 'text-gray-500' },
  { value: 'accountant', label: () => t('users.accountant'), icon: BookOpen, color: 'text-blue-500' },
]

export function UserFormPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditing = !!id
  const [avatarSelectorOpen, setAvatarSelectorOpen] = useState(false)
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null)

  const { data: user, isLoading: isLoadingUser } = useUser(Number(id))
  const createUser = useCreateUser()
  const updateUser = useUpdateUser()

  const form = useForm<UserFormValues>({
    resolver: zodResolver(createUserSchema(isEditing)),
    defaultValues: {
      full_name: '',
      email: '',
      role: 'seller',
      password: '',
      is_active: true,
    },
  })

  // Load user data when editing
  useEffect(() => {
    if (user) {
      form.reset({
        full_name: user.full_name,
        email: user.email || '',
        // Los roles viejos (gerente, cajero) trabajan como vendedor.
        role: (user.role === 'admin' || user.role === 'accountant' ? user.role : 'seller') as 'admin' | 'seller' | 'accountant',
        password: '',
        is_active: user.is_active,
      })
      setSelectedAvatar(user.avatar || null)
    }
  }, [user, form])

  const onSubmit = async (data: UserFormValues) => {
    try {
      if (isEditing) {
        const updateData = {
          full_name: data.full_name,
          email: data.email,
          role: data.role as UserRole,
          is_active: data.is_active,
          avatar: selectedAvatar || undefined,
          ...(data.password ? { password: data.password } : {}),
        }
        await updateUser.mutateAsync({ id: Number(id), data: updateData })
      } else {
        const createData = {
          full_name: data.full_name,
          email: data.email,
          role: data.role as UserRole,
          password: data.password!,
          avatar: selectedAvatar || undefined,
        }
        await createUser.mutateAsync(createData)
      }
      navigate('/users')
    } catch (error) {
      form.setError('root', { message: parseApiError(error) })
    }
  }

  const handleAvatarSelect = (avatarId: string) => {
    setSelectedAvatar(avatarId)
    setAvatarSelectorOpen(false)
  }

  const isLoading = createUser.isPending || updateUser.isPending

  if (isEditing && isLoadingUser) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-4 pb-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl md:text-2xl font-bold">
          {isEditing ? t('users.editUser') : t('users.newUser')}
        </h1>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {form.formState.errors.root && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
              {form.formState.errors.root.message}
            </div>
          )}

          {/* Avatar Selection */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('users.selectAvatar')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <UserAvatar
                    avatarId={selectedAvatar}
                    name={form.watch('full_name') || 'Usuario'}
                    size="xl"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full shadow-md"
                    onClick={() => setAvatarSelectorOpen(true)}
                  >
                    <Camera className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">{t('users.changeAvatar')}</p>
                  <p className="text-xs text-muted-foreground">
                    {t('users.selectAvatarDescription')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('users.accountInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('users.email')} *</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        className="h-12 text-base"
                        autoComplete="username"
                      />
                    </FormControl>
                    <FormDescription className="text-xs">
                      {t('users.emailIsLogin')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('users.password')} {!isEditing && '*'}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="password"
                        className="h-12 text-base"
                        autoComplete={isEditing ? 'new-password' : 'new-password'}
                      />
                    </FormControl>
                    {isEditing && (
                      <FormDescription className="text-xs">
                        {t('users.keepCurrentPassword')}
                      </FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Personal Info */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('users.personalInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="full_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('users.fullName')} *</FormLabel>
                    <FormControl>
                      <Input {...field} className="h-12 text-base" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

            </CardContent>
          </Card>

          {/* Role & Status */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('users.roleAndStatus')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('users.role')} *</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="h-12">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {roles.map((role) => {
                          const Icon = role.icon
                          return (
                            <SelectItem key={role.value} value={role.value}>
                              <div className="flex items-center gap-2">
                                <Icon className={`h-4 w-4 ${role.color}`} />
                                {role.label()}
                              </div>
                            </SelectItem>
                          )
                        })}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        className="h-6 w-6"
                      />
                    </FormControl>
                    <div className="space-y-0.5">
                      <FormLabel className="!mt-0 text-base">{t('common.active')}</FormLabel>
                      <FormDescription className="text-xs">
                        {t('users.activeDescription')}
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="h-12 sm:flex-1"
              onClick={() => navigate(-1)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" className="h-12 sm:flex-1" disabled={isLoading}>
              {isLoading && <Spinner size="sm" className="mr-2" />}
              {isEditing ? t('common.update') : t('users.createUser')}
            </Button>
          </div>
        </form>
      </Form>

      <AvatarSelector
        open={avatarSelectorOpen}
        onOpenChange={setAvatarSelectorOpen}
        currentAvatarId={selectedAvatar}
        onSelect={handleAvatarSelect}
      />
    </div>
  )
}
